import { Subscription } from '@apollo/client/react/components';
import React, { useCallback } from 'react';
import styled from 'styled-components';
import { roomSubscription } from '@/graph/rooms';
import get from 'lodash/get';
import ProgressRing from './ProgressRing';
import colors from '../../../../colors';
import { formatTime, useFiltrationProgress, useSoundAlert } from './utils';
import { TimerOuterRim } from './Common';
import { defaultTo } from 'lodash/util';
import { AirFilterTimeModuleOptional } from '../../../ModuleOptional';
import { useCountdown, useCurrentTime } from '@/util/timerHooks';

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 0.8rem;
`;

const Counter = styled.label`
  font-family: monospace;
  color: ${colors.orange.string()};
  font-size: 0.7rem;
  font-weight: 600;
`;

const IndicatorSimple = ({ airFiltrationEndsAt, warningEndAt, warningDuration, style, radius = 15 }) => {
  const progress = useCallback(
    now => useFiltrationProgress({ now, warningEndAt, warningDuration }),
    [warningEndAt, warningDuration]
  );

  return (
    <Container style={style}>
      <ProgressRing
        style={{ position: 'absolute' }}
        color={colors.orange.alpha(0.9).string()}
        radius={radius}
        stroke={2}
        progress={progress}
      />
      <Counter>
        <Difference airFiltrationEndsAt={airFiltrationEndsAt} />
      </Counter>
    </Container>
  );
};

const Difference = ({ airFiltrationEndsAt }) => {
  // Use the proper useCountdown hook for countdown logic
  const { remainingMinutes } = useCountdown({
    endTime: airFiltrationEndsAt,
    interval: 1000,
    precision: 'second',
  });

  return <>{Math.max(remainingMinutes, 0)}</>;
};

const IndicatorTimer = ({ warningEndAt, warningDuration, style, radius = 46, stroke = 4 }) => {
  const progress = useCallback(
    now => useFiltrationProgress({ now, warningEndAt, warningDuration }),
    [warningEndAt, warningDuration]
  );

  return (
    <TimerOuterRim style={style}>
      <ProgressRing
        style={{ position: 'absolute' }}
        color={colors.orange.alpha(0.9).string()}
        radius={radius}
        stroke={stroke}
        progress={progress}
      />
      <Counter>
        <Time warningEndAt={warningEndAt} />
      </Counter>
    </TimerOuterRim>
  );
};

const Time = ({ warningEndAt }) => {
  // Use the proper useCurrentTime hook for current time display
  const now = useCurrentTime({
    interval: 1000,
    precision: 'second',
  });
  return <>{formatTime(now, warningEndAt)}</>;
};

const Switch = ({ Component, airFiltrationEndsAt, airFiltrationDurationMin, airFiltrationWarningOn, ...rest }) => {
  useSoundAlert(airFiltrationWarningOn);

  if (!airFiltrationWarningOn) {
    return null;
  }

  return (
    <Component
      {...rest}
      airFiltrationWarningOn={airFiltrationWarningOn}
      warningEndAt={airFiltrationEndsAt}
      warningDuration={defaultTo(airFiltrationDurationMin, 20) * 60}
    />
  );
};

const RoomData =
  Component =>
  ({ roomId, ...rest }) => (
    <Subscription subscription={roomSubscription} variables={{ roomId: roomId }} skip={!roomId}>
      {({ data }) => <Switch Component={Component} {...rest} {...get(data, 'room', {})} />}
    </Subscription>
  );

export const AirFiltrationIndicatorTimer = AirFilterTimeModuleOptional(RoomData(IndicatorTimer));
export const AirFiltrationIndicatorSimple = AirFilterTimeModuleOptional(RoomData(IndicatorSimple));
